import {
  CreateResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetOneParams,
  GetOneResult,
  GetListParams,
  GetListResult,
  UpdateResult,
  UpdateParams,
  DeleteResult,
  DeleteParams,
} from "react-admin";
import { serverAPI, RoomDto } from "../api/server.api";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { splitCompositeId } from "../backoffice.utils";

export interface Room {
  id: string;
  createdAt: string | null;
  createdById: string;
  updatedAt: string | null;
  updatedById: string;
  drivingSchoolId: string;
  branchId: string;
  name: string;
  description: string;
  numberOfSeats: number;
}

class RoomsProvider extends AbstractDataProvider<Room> {
  async getOne(_: string, { id }: GetOneParams<Room>): Promise<GetOneResult<Room>> {
    const [drivingSchoolId, recordId] = splitCompositeId(id);
    const room = await serverAPI.getRoom({ id: recordId, drivingSchoolId });
    return { data: _toRecord(room) };
  }

  async getList(_resource: string, params: GetListParams): Promise<GetListResult<Room>> {
    const { filter, sort, pagination } = params;
    const { drivingSchoolId, ...clientSideFilter } = filter;
    if (!drivingSchoolId) {
      throw new Error(`Unexpected filter: ${JSON.stringify(filter)} -- expected: {"drivingSchoolId": ...}`);
    }
    const rooms = await serverAPI.getRooms({ drivingSchoolId });
    return applyPagination(applySort(applyFilter(rooms.map(_toRecord), clientSideFilter), sort), pagination);
  }

  async getManyReference(
    _resource: string,
    { target, id, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<Room>> {
    if (target !== "drivingSchoolId") {
      throw new Error(`Invalid target: ${JSON.stringify(target)} -- expected: "drivingSchoolId"`);
    }
    return this.getList("rooms", { filter: { drivingSchoolId: id, ...filter }, sort, pagination });
  }

  async create(
    _resource: string,
    params: {
      data: Omit<Room, "id" | "createdAt" | "createdById" | "updatedAt" | "updatedBy">;
      meta: Record<string, any>;
    },
  ): Promise<CreateResult<Room>> {
    const { branchId, name, description, numberOfSeats } = params.data;
    const { drivingSchoolId } = params.meta;
    const room = await serverAPI.createRoom({
      drivingSchoolId,
      payload: {
        branchId,
        name,
        description: description ?? "",
        numberOfSeats,
      },
    });
    return { data: _toRecord(room) };
  }

  async update(_resource: string, params: UpdateParams<Room>): Promise<UpdateResult<Room>> {
    const { id, data } = params;
    const [drivingSchoolId, recordId] = splitCompositeId(id);
    const { name, description, numberOfSeats, branchId } = data;
    const room = await serverAPI.updateRoom({
      id: recordId,
      drivingSchoolId,
      payload: {
        name,
        description: description ?? "",
        numberOfSeats,
        branchId,
      },
    });
    return { data: _toRecord(room) };
  }

  async delete(_resource: string, params: DeleteParams<Room>): Promise<DeleteResult> {
    const { id } = params;
    const [drivingSchoolId, recordId] = splitCompositeId(id);
    await serverAPI.deleteRoom({ id: recordId, drivingSchoolId });
    return { data: {} };
  }
}

export const roomsProvider = new RoomsProvider();

function _toRecord(dto: RoomDto): Room {
  return {
    ...dto,
    id: `${dto.drivingSchoolId}:${dto.id}`,
    description: dto.description ?? "",
    branchId: dto.branchId ?? "",
  };
}
